import { componentQrl, inlinedQrl, _jsxBranch, useStore, useContextProvider, useTaskQrl, useLexicalScope, _jsxC, _IMMUTABLE, _fnSignal } from "@builder.io/qwik";
import { getDefaultRegisteredComponents } from "../../constants/builder-registered-components.qwik.mjs";
import ComponentsContext from "../../context/components.context.qwik.mjs";
import { evaluate } from "../../functions/evaluate/evaluate.qwik.mjs";
import { serializeIncludingFunctions } from "../../functions/register-component.qwik.mjs";
import { logger } from "../../helpers/logger.qwik.mjs";
import { Blocks } from "../blocks/blocks.qwik.mjs";
import { getUpdateVariantVisibilityScript } from "../content-variants/helpers.qwik.mjs";
import { InlinedScript } from "../inlined-script.qwik.mjs";
import { EnableEditor } from "./components/enable-editor.qwik.mjs";
import { ContentStyles } from "./components/styles.qwik.mjs";
import { getContentInitialValue, getRootStateInitialValue } from "./content.helpers.qwik.mjs";
const ContentComponent = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  var _a, _b;
  _jsxBranch();
  const state = useStore({
    builderContextSignal: {
      content: getContentInitialValue({
        content: JSON.parse(JSON.stringify(props.content || {})),
        data: props.data
      }),
      localState: void 0,
      rootState: getRootStateInitialValue({
        content: props.content,
        data: props.data,
        locale: props.locale
      }),
      rootSetState: void 0,
      context: props.context || {},
      canTrack: props.canTrack,
      apiKey: props.apiKey,
      apiVersion: props.apiVersion,
      componentInfos: [
        ...getDefaultRegisteredComponents(),
        ...props.customComponents || []
      ].reduce((acc, { component: _, ...info }) => ({
        ...acc,
        [info.name]: serializeIncludingFunctions(info)
      }), {}),
      inheritedStyles: {},
      BlocksWrapper: props.blocksWrapper || "div",
      BlocksWrapperProps: props.blocksWrapperProps || {},
      nonce: props.nonce || "",
      model: props.model
    },
    registeredComponents: [
      ...getDefaultRegisteredComponents(),
      ...props.customComponents || []
    ].reduce((acc, { component, ...info }) => ({
      ...acc,
      [info.name]: {
        component,
        ...serializeIncludingFunctions(info)
      }
    }), {}),
    scriptStr: getUpdateVariantVisibilityScript({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      variationId: (_a = props.content) == null ? void 0 : _a.testVariationId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      contentId: (_b = props.content) == null ? void 0 : _b.id
    })
  }, {
    deep: true
  });
  useContextProvider(ComponentsContext, useStore({
    registeredComponents: state.registeredComponents
  }));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2, _b2;
    const [props2, state2] = useLexicalScope();
    if (!props2.apiKey)
      logger.error("No API key provided to `Content` component. This can cause issues. Please provide an API key using the `apiKey` prop.");
    const jsCode = (_b2 = (_a2 = state2.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.jsCode;
    if (jsCode)
      evaluate({
        code: jsCode,
        context: props2.context || {},
        localState: void 0,
        rootState: state2.builderContextSignal.rootState,
        rootSetState: (newState) => {
          var _a3, _b3;
          (_b3 = (_a3 = state2.builderContextSignal).rootSetState) == null ? void 0 : _b3.call(_a3, newState);
        },
        isExpression: false
      });
  }, "ContentComponent_component_useTask_DB1dCCWPU8M", [
    props,
    state
  ]));
  return /* @__PURE__ */ _jsxC(EnableEditor, {
    get apiHost() {
      return props.apiHost;
    },
    get nonce() {
      return props.nonce;
    },
    get content() {
      return props.content;
    },
    get data() {
      return props.data;
    },
    get model() {
      return props.model;
    },
    get context() {
      return props.context;
    },
    get apiKey() {
      return props.apiKey;
    },
    get canTrack() {
      return props.canTrack;
    },
    get locale() {
      return props.locale;
    },
    get enrich() {
      return props.enrich;
    },
    get showContent() {
      return props.showContent;
    },
    get builderContextSignal() {
      return state.builderContextSignal;
    },
    get contentWrapper() {
      return props.contentWrapper;
    },
    get contentWrapperProps() {
      return props.contentWrapperProps;
    },
    get trustedHosts() {
      return props.trustedHosts;
    },
    get isNestedRender() {
      return props.isNestedRender;
    },
    children: [
      props.isSsrAbTest ? /* @__PURE__ */ _jsxC(InlinedScript, {
        id: "builderio-variant-visibility",
        get scriptStr() {
          return state.scriptStr;
        },
        get nonce() {
          return props.nonce || "";
        },
        [_IMMUTABLE]: {
          id: _IMMUTABLE,
          nonce: _fnSignal((p0) => p0.nonce || "", [
            props
          ], 'p0.nonce||""'),
          scriptStr: _fnSignal((p0) => p0.scriptStr, [
            state
          ], "p0.scriptStr")
        }
      }, 3, "LQ_0") : null,
      /* @__PURE__ */ _jsxC(ContentStyles, {
        get nonce() {
          return props.nonce || "";
        },
        get isNestedRender() {
          return props.isNestedRender;
        },
        get contentId() {
          var _a2;
          return (_a2 = state.builderContextSignal.content) == null ? void 0 : _a2.id;
        },
        get cssCode() {
          var _a2, _b2;
          return (_b2 = (_a2 = state.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.cssCode;
        },
        get customFonts() {
          var _a2, _b2;
          return (_b2 = (_a2 = state.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.customFonts;
        },
        [_IMMUTABLE]: {
          contentId: _fnSignal((p0) => {
            var _a2;
            return (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.id;
          }, [
            state
          ], "p0.builderContextSignal.content?.id"),
          cssCode: _fnSignal((p0) => {
            var _a2, _b2;
            return (_b2 = (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.cssCode;
          }, [
            state
          ], "p0.builderContextSignal.content?.data?.cssCode"),
          customFonts: _fnSignal((p0) => {
            var _a2, _b2;
            return (_b2 = (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.customFonts;
          }, [
            state
          ], "p0.builderContextSignal.content?.data?.customFonts"),
          isNestedRender: _fnSignal((p0) => p0.isNestedRender, [
            props
          ], "p0.isNestedRender"),
          nonce: _fnSignal((p0) => p0.nonce || "", [
            props
          ], 'p0.nonce||""')
        }
      }, 3, "LQ_1"),
      /* @__PURE__ */ _jsxC(Blocks, {
        get blocks() {
          var _a2, _b2;
          return (_b2 = (_a2 = state.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.blocks;
        },
        get context() {
          return state.builderContextSignal;
        },
        get registeredComponents() {
          return state.registeredComponents;
        },
        get linkComponent() {
          return props.linkComponent;
        },
        [_IMMUTABLE]: {
          blocks: _fnSignal((p0) => {
            var _a2, _b2;
            return (_b2 = (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.data) == null ? void 0 : _b2.blocks;
          }, [
            state
          ], "p0.builderContextSignal.content?.data?.blocks"),
          context: _fnSignal((p0) => p0.builderContextSignal, [
            state
          ], "p0.builderContextSignal"),
          linkComponent: _fnSignal((p0) => p0.linkComponent, [
            props
          ], "p0.linkComponent"),
          registeredComponents: _fnSignal((p0) => p0.registeredComponents, [
            state
          ], "p0.registeredComponents")
        }
      }, 3, "LQ_2")
    ],
    [_IMMUTABLE]: {
      apiHost: _fnSignal((p0) => p0.apiHost, [
        props
      ], "p0.apiHost"),
      apiKey: _fnSignal((p0) => p0.apiKey, [
        props
      ], "p0.apiKey"),
      builderContextSignal: _fnSignal((p0) => p0.builderContextSignal, [
        state
      ], "p0.builderContextSignal"),
      canTrack: _fnSignal((p0) => p0.canTrack, [
        props
      ], "p0.canTrack"),
      content: _fnSignal((p0) => p0.content, [
        props
      ], "p0.content"),
      contentWrapper: _fnSignal((p0) => p0.contentWrapper, [
        props
      ], "p0.contentWrapper"),
      contentWrapperProps: _fnSignal((p0) => p0.contentWrapperProps, [
        props
      ], "p0.contentWrapperProps"),
      context: _fnSignal((p0) => p0.context, [
        props
      ], "p0.context"),
      data: _fnSignal((p0) => p0.data, [
        props
      ], "p0.data"),
      enrich: _fnSignal((p0) => p0.enrich, [
        props
      ], "p0.enrich"),
      isNestedRender: _fnSignal((p0) => p0.isNestedRender, [
        props
      ], "p0.isNestedRender"),
      locale: _fnSignal((p0) => p0.locale, [
        props
      ], "p0.locale"),
      model: _fnSignal((p0) => p0.model, [
        props
      ], "p0.model"),
      nonce: _fnSignal((p0) => p0.nonce, [
        props
      ], "p0.nonce"),
      showContent: _fnSignal((p0) => p0.showContent, [
        props
      ], "p0.showContent"),
      trustedHosts: _fnSignal((p0) => p0.trustedHosts, [
        props
      ], "p0.trustedHosts")
    }
  }, 1, "LQ_3");
}, "ContentComponent_component_HIsczUcxjCE"));
export {
  ContentComponent,
  ContentComponent as default
};
